import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import api from '../services/api';
import './FeedbackManager.css';

const FeedbackManager = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    rating: '',
    category: '',
    priority: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0
  });
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchFeedbacks();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.current_page]);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: pagination.current_page,
        limit: 20,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      // Use the axios api client so the interceptor attaches the admin Bearer
      // token — optionalAdminTenant resolves the tenant (tf_tenant_virq) from
      // the admin JWT. Raw fetch sent no headers, so the tenant never resolved
      // and the tenant-scoped controller returned 400.
      const response = await api.get(`/api/v1/feedback?${queryParams}`);
      const data = response.data;

      if (data.success) {
        // Ensure attachments are parsed and validated
        const processedFeedbacks = data.data.map(fb => {
          // Parse attachments if it's a string (JSON from database)
          let attachments = fb.attachments;
          if (attachments) {
            if (typeof attachments === 'string') {
              try {
                attachments = JSON.parse(attachments);
              } catch (e) {
                console.warn(`Failed to parse attachments for feedback #${fb.id}:`, e);
                attachments = null;
              }
            }
            // Ensure attachments is an array
            if (!Array.isArray(attachments)) {
              attachments = null;
            }
          }
          return {
            ...fb,
            attachments: attachments || null
          };
        });
        
        // Debug: Log feedbacks with attachments
        console.log('📋 Fetched feedbacks:', processedFeedbacks.length);
        processedFeedbacks.forEach(fb => {
          if (fb.attachments && Array.isArray(fb.attachments) && fb.attachments.length > 0) {
            console.log(`✅ Feedback #${fb.id} has ${fb.attachments.length} attachment(s):`, fb.attachments);
          }
        });
        
        setFeedbacks(processedFeedbacks);
        setPagination(data.pagination);
      } else {
        console.error('Error fetching feedbacks:', data.message);
      }
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/v1/feedback/stats');
      const data = response.data;

      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const updateFeedback = async (id, updateData) => {
    try {
      const response = await api.put(`/api/v1/feedback/${id}`, updateData);

      const data = response.data;

      if (data.success) {
        fetchFeedbacks(); // Refresh the list
        setShowModal(false);
        setSelectedFeedback(null);
      } else {
        alert('Error updating feedback: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating feedback:', error);
      alert('Error updating feedback');
    }
  };

  const deleteFeedback = async (id) => {
    if (window.confirm('Are you sure you want to delete this feedback?')) {
      try {
        const response = await api.delete(`/api/v1/feedback/${id}`);

        const data = response.data;

        if (data.success) {
          fetchFeedbacks(); // Refresh the list
        } else {
          alert('Error deleting feedback: ' + data.message);
        }
      } catch (error) {
        console.error('Error deleting feedback:', error);
        alert('Error deleting feedback');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'status-pending',
      reviewed: 'status-reviewed',
      resolved: 'status-resolved',
      dismissed: 'status-dismissed'
    };
    return colors[status] || 'status-pending';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'priority-low',
      medium: 'priority-medium',
      high: 'priority-high',
      urgent: 'priority-urgent'
    };
    return colors[priority] || 'priority-medium';
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span
        key={i}
        className={`star ${i < rating ? 'filled' : ''}`}
      >
        ★
      </span>
    ));
  };

  const handleFeedbackClick = (feedback) => {
    console.log('🔍 Opening feedback modal:', feedback.id);
    console.log('📎 Attachments in feedback:', feedback.attachments);
    setSelectedFeedback(feedback);
    setShowModal(true);
  };

  const handleModalSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const updateData = {
      status: formData.get('status'),
      priority: formData.get('priority'),
      admin_notes: formData.get('admin_notes'),
      reviewed_by: 1 // Assuming admin user ID is 1
    };
    
    updateFeedback(selectedFeedback.id, updateData);
  };

  if (loading) {
    return (
      <div className="feedback-manager">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading feedbacks...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="feedback-manager">
      {/* Header */}
      <div className="feedback-header">
        <h1>Feedback Manager</h1>
        <p>Manage user feedback and ratings</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>Total Feedbacks</h3>
            <p className="stat-number">{stats.total_feedbacks || 0}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <h3>Average Rating</h3>
            <p className="stat-number">{stats.average_rating || '0.0'}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👁️</div>
          <div className="stat-content">
            <h3>Pending Reviews</h3>
            <p className="stat-number">{stats.pending_feedbacks || 0}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🚨</div>
          <div className="stat-content">
            <h3>Urgent</h3>
            <p className="stat-number">{stats.urgent_count || 0}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Status</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Rating</label>
          <select
            value={filters.rating}
            onChange={(e) => setFilters({...filters, rating: e.target.value})}
          >
            <option value="">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Category</label>
          <select
            value={filters.category}
            onChange={(e) => setFilters({...filters, category: e.target.value})}
          >
            <option value="">All Categories</option>
            <option value="bug_report">Bug Report</option>
            <option value="feature_request">Feature Request</option>
            <option value="general_feedback">General Feedback</option>
            <option value="complaint">Complaint</option>
            <option value="compliment">Compliment</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Priority</label>
          <select
            value={filters.priority}
            onChange={(e) => setFilters({...filters, priority: e.target.value})}
          >
            <option value="">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Feedback List */}
      <div className="feedbacks-section">
        <div className="section-header">
          <h3>Feedbacks ({feedbacks.length})</h3>
        </div>

        <div className="feedbacks-list">
          {feedbacks.map((feedback) => (
            <div key={feedback.id} className="feedback-card" onClick={() => handleFeedbackClick(feedback)}>
              <div className="feedback-header">
                <div className="feedback-rating">
                  {renderStars(feedback.rating)}
                </div>
                <div className="feedback-badges">
                  <span className={`status-badge ${getStatusColor(feedback.status)}`}>
                    {feedback.status}
                  </span>
                  <span className={`priority-badge ${getPriorityColor(feedback.priority)}`}>
                    {feedback.priority}
                  </span>
                  <span className="category-badge">
                    {feedback.feedback_category.replace('_', ' ')}
                  </span>
                </div>
              </div>

              <div className="feedback-content">
                <div className="feedback-user">
                  <strong>{feedback.user.name}</strong> ({feedback.user.email})
                  <span className="feedback-date">
                    {new Date(feedback.created_at).toLocaleDateString()}
                  </span>
                </div>

                {feedback.feedback_text && (
                  <p className="feedback-text">{feedback.feedback_text}</p>
                )}

                {/* Display attachments if available */}
                {feedback.attachments && Array.isArray(feedback.attachments) && feedback.attachments.length > 0 && (
                  <div className="feedback-attachments">
                    <strong>Attachments ({feedback.attachments.length}):</strong>
                    <div className="attachments-grid">
                      {feedback.attachments.map((attachment, idx) => (
                        <div key={idx} className="attachment-item">
                          {attachment.mimeType.startsWith('image/') ? (
                            <img
                              src={attachment.url}
                              alt={attachment.originalName}
                              className="attachment-thumbnail"
                              onClick={() => window.open(attachment.url, '_blank')}
                            />
                          ) : (
                            <a
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="attachment-link"
                            >
                              📎 {attachment.originalName}
                              <span className="file-size">({(attachment.size / 1024).toFixed(1)} KB)</span>
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Backward compatibility: display photo_url if no attachments */}
                {(!feedback.attachments || feedback.attachments.length === 0) && feedback.photo_url && (
                  <div className="feedback-photo">
                    <img
                      src={feedback.photo_url}
                      alt={`Feedback from ${feedback.name || 'user'}`}
                      className="photo-thumbnail"
                    />
                  </div>
                )}

                {feedback.admin_notes && (
                  <div className="admin-notes">
                    <strong>Admin Notes:</strong> {feedback.admin_notes}
                  </div>
                )}
              </div>

              <div className="feedback-actions">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateFeedback(feedback.id, { status: 'reviewed' });
                  }}
                  className="btn btn-primary"
                >
                  Mark Reviewed
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateFeedback(feedback.id, { status: 'resolved' });
                  }}
                  className="btn btn-success"
                >
                  Mark Resolved
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteFeedback(feedback.id);
                  }}
                  className="btn btn-danger"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setPagination({...pagination, current_page: pagination.current_page - 1})}
              disabled={!pagination.has_prev}
              className="btn btn-secondary"
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.current_page} of {pagination.total_pages}
            </span>
            <button
              onClick={() => setPagination({...pagination, current_page: pagination.current_page + 1})}
              disabled={!pagination.has_next}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Modal for detailed feedback management */}
      {showModal && selectedFeedback && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manage Feedback #{selectedFeedback.id}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleModalSubmit} className="modal-form">
              {/* User Information Section */}
              <div className="user-info-section">
                <h4>User Information</h4>
                <div className="user-details">
                  <div className="user-detail-item">
                    <strong>Username:</strong> {selectedFeedback.user.name}
                  </div>
                  <div className="user-detail-item">
                    <strong>Email:</strong> {selectedFeedback.user.email}
                  </div>
                  <div className="user-detail-item">
                    <strong>User ID:</strong> {selectedFeedback.user.id}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select name="status" defaultValue={selectedFeedback.status}>
                  <option value="pending">Pending</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="resolved">Resolved</option>
                  <option value="dismissed">Dismissed</option>
                </select>
              </div>

              <div className="form-group">
                <label>Priority</label>
                <select name="priority" defaultValue={selectedFeedback.priority}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              {/* Attachments Section */}
              {selectedFeedback.attachments && Array.isArray(selectedFeedback.attachments) && selectedFeedback.attachments.length > 0 && (
                <div className="form-group attachments-section">
                  <label>Attachments ({selectedFeedback.attachments.length})</label>
                  <div className="attachments-list">
                    {selectedFeedback.attachments.map((attachment, idx) => (
                      <div key={idx} className="attachment-item-detailed">
                        {attachment.mimeType.startsWith('image/') ? (
                          <div className="attachment-image">
                            <img
                              src={attachment.url}
                              alt={attachment.originalName}
                              className="attachment-preview"
                              onClick={() => window.open(attachment.url, '_blank')}
                            />
                            <div className="attachment-info">
                              <strong>{attachment.originalName}</strong>
                              <span className="file-meta">
                                {(attachment.size / 1024).toFixed(1)} KB • {attachment.mimeType}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="attachment-file-link"
                          >
                            <span className="file-icon">📎</span>
                            <div className="file-details">
                              <strong>{attachment.originalName}</strong>
                              <span className="file-meta">
                                {(attachment.size / 1024).toFixed(1)} KB • {attachment.mimeType}
                              </span>
                            </div>
                            <span className="download-icon">⬇️</span>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Admin Notes</label>
                <textarea
                  name="admin_notes"
                  defaultValue={selectedFeedback.admin_notes || ''}
                  rows="4"
                  placeholder="Add admin notes or response..."
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Update Feedback</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
};

export default FeedbackManager;
